import React, { useState, useEffect } from 'react';
import './App.css';
import FloorplanViewer from './components/FloorplanViewer';
import Changelog from './components/Changelog';
import DragDropFileUpload from './components/DragDropFileUpload';
import FileUploadInfo from './components/FileUploadInfo';
import SignUp from './components/Auth/SignUp';
import Login from './components/Auth/Login';
import AuthGuard from './components/Auth/AuthGuard';
import AuthCallback from './components/Auth/AuthCallback';
import ProjectList from './components/Projects/ProjectList';
import ProjectNameInput from './components/Projects/ProjectNameInput';
import SaveStatus from './components/SaveStatus';
import SettingsPage from './pages/SettingsPage';
import PresetsPage from './pages/PresetsPage';
import ProjectSummaryPage from './pages/ProjectSummaryPage';
// import SimpleFloorplan from './pages/SimpleFloorplan';
import GlobalDragDrop from './components/GlobalDragDrop';
import { useAuth } from './contexts/AuthContext';
import { PricingProvider } from './contexts/PricingContext';
import { saveProject, getProject } from './utils/supabaseClient';
import { 
  APP_VERSION, 
  getCurrentVersionChanges,
  getCurrentVersionDate
} from './utils/versionData';

function App() {
  const [imageUrl, setImageUrl] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // Default to login view
  const [currentProject, setCurrentProject] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'saved', 'error', or null
  const [showSettings, setShowSettings] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [activeView, setActiveView] = useState('projects'); // 'projects', 'upload', 'viewer', 'settings', 'summary', 'simple'
  
  // Get authentication state from context
  const { user, signOut } = useAuth();

  // Check if this is an auth callback URL
  const isAuthCallback = window.location.pathname === '/auth/callback' || 
                         window.location.hash.includes('access_token') ||
                         window.location.search.includes('error');

  // Listen for auth state changes
  useEffect(() => {
    if (user) {
      setAuthMode(null);
    }
  }, [user]);
  
  // Handle showing presets page
  const handleShowPresets = () => {
    setActiveView('presets');
  };

  // Handle going back from presets page
  const handleBackFromPresets = () => {
    // Return to previous view
    if (imageUrl) {
      setActiveView('viewer');
    } else {
      setActiveView('projects');
    }
  };
  
  // Add event listener for show-presets event
  useEffect(() => {
    const handleShowPresetsEvent = () => {
      handleShowPresets();
    };
    
    window.addEventListener('show-presets', handleShowPresetsEvent);
    
    return () => {
      window.removeEventListener('show-presets', handleShowPresetsEvent);
    };
  }, [handleShowPresets]);

  // Add keyboard shortcut for presets (Command+I / Ctrl+I)
  useEffect(() => {
    const handleKeyboardShortcut = (e) => {
      // Check for Command+I on Mac or Ctrl+I on Windows
      if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
        e.preventDefault(); // Prevent default browser action
        handleShowPresets();
        console.log('Presets shortcut activated');
      }
    };
    
    window.addEventListener('keydown', handleKeyboardShortcut);
    
    return () => {
      window.removeEventListener('keydown', handleKeyboardShortcut);
    };
  }, [handleShowPresets]);

  // Handle closing the changelog
  const handleCloseChangelog = () => {
    setShowChangelog(false);
  };

  // Handle file selection (from drag-drop or file input)
  const handleFileSelect = (file) => {
    if (file) {
      // Track if this was originally a PDF (useful for analytics later)
      const wasPdf = file.name.includes('-page1.png') && file.name.includes('_');
      
      setFileName(wasPdf ? `PDF: ${file.name.replace('-page1.png', '.pdf')}` : file.name);
      
      // Show processing indicator
      setIsProcessing(true);
      
      const reader = new FileReader();
      reader.onload = () => {
        setImageUrl(reader.result);
        
        // Give a brief moment to allow the UI to update
        setTimeout(() => {
          setIsProcessing(false);
        }, 800);
      };
      
      reader.onerror = () => {
        alert('Error reading the file. Please try again with a different file.');
        setIsProcessing(false);
      };
      
      reader.readAsDataURL(file);
    }
  };

  // Reset the app
  const handleReset = () => {
    setImageUrl(null);
    setFileName('');
    setCurrentProject(null);
    setShowSettings(false);
  };

  // Handle authentication mode changes
  const handleAuthModeChange = (mode) => {
    setAuthMode(mode);
  };

  // Handle successful authentication
  const handleAuthSuccess = (nextMode) => {
    if (nextMode) {
      setAuthMode(nextMode);
    } else {
      setAuthMode(null);
    }
  };

  // Handle sign out
  const handleSignOut = async () => {
    await signOut();
    handleReset();
    setAuthMode('login');
  };

  // Function to handle project selection
  const handleSelectProject = (project) => {
    console.log('ENTRY DEBUG - Selected project:', project);
    
    // Create a normalized project object that ensures consistent field names
    const normalizedProject = {
      ...project,
      // Handle both naming conventions for backward compatibility
      entryPoints: project.entryPoints || project.entry_points || []
    };
    
    setCurrentProject(normalizedProject);
    setImageUrl(project.image_url);
    setFileName(project.name);
    setActiveView('viewer');
    
    // Check both properties to handle legacy projects
    console.log('ENTRY DEBUG - Entry points in selected project:', normalizedProject.entryPoints);
    
    // Close settings if open
    setShowSettings(false);
    setShowSummary(false);
  };

  // Handle project name changes
  const handleProjectNameChange = (newName) => {
    setFileName(newName);
    
    // If we have a current project, update its name
    if (currentProject) {
      setCurrentProject(prev => ({ ...prev, name: newName }));
    }
  };
  
  // Handle project saving
  const handleSaveProject = async (projectData) => {
    console.log('ENTRY DEBUG - App handling save project with data:', projectData);
    console.log('ENTRY DEBUG - Entry points in project data being saved:', projectData.entryPoints);
    
    try {
      // Prepare data for saving
      const dataToSave = {
        ...projectData,
        name: fileName || 'Untitled Project',
        imageUrl: imageUrl,
        id: currentProject?.id,
        // Make sure entry_points is also set for database consistency
        entryPoints: projectData.entryPoints || [],
        entry_points: projectData.entryPoints || []
      };
      
      // Call the API function to save the project
      const savedProject = await saveProject(dataToSave);
      
      // Update current project with saved data - ensure consistent field naming
      const normalizedProject = {
        ...savedProject,
        entryPoints: savedProject.entryPoints || savedProject.entry_points || []
      };
      
      setCurrentProject(normalizedProject);
      
      console.log('ENTRY DEBUG - Project saved successfully:', normalizedProject);
      console.log('ENTRY DEBUG - Entry points in saved project:', normalizedProject.entryPoints);
      
      return savedProject;
    } catch (error) {
      console.error('Error saving project:', error);
      return null;
    }
  };

  // Handle unauthenticated access
  const handleUnauthenticated = () => {
    setAuthMode('login');
  };

  // Handle toggling settings
  const handleToggleSettings = () => {
    setActiveView('settings');
  };

  // Handle showing the project summary
  const handleShowSummary = () => {
    setActiveView('summary');
  };

  // Handle going back from summary page
  const handleBackFromSummary = () => {
    setActiveView('viewer');
  };

  // Add console message about keyboard shortcut for presets
  useEffect(() => {
    console.log(
      '%cTIP: Press Command+I (Mac) or Ctrl+I (Windows) to access pricing presets',
      'color: #4CAF50; font-weight: bold; font-size: 14px;'
    );
  }, []);

  // Add state to control when global drag and drop should be active
  const [globalDragDropEnabled, setGlobalDragDropEnabled] = useState(false);
  
  // Effect to enable/disable global drag and drop based on active view
  useEffect(() => {
    // Enable global drag and drop only on the projects page
    setGlobalDragDropEnabled(activeView === 'projects' && !authMode);
  }, [activeView, authMode]);

  // Add URL hash management
  useEffect(() => {
    // Handle initial URL hash on load
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '') || 'projects';
      
      // Don't change view for auth callbacks
      if (isAuthCallback) return;
      
      // Map hash to view state
      switch (hash) {
        case 'viewer':
          if (imageUrl) setActiveView('viewer');
          break;
        case 'settings':
          setShowSettings(true);
          setActiveView('settings');
          break;
        case 'summary':
          setShowSummary(true);
          setActiveView('summary');
          break;
        case 'presets':
          setShowPresets(true);
          setActiveView('presets');
          break;
        default:
          setActiveView('projects');
      }
    };
    
    // Set initial state based on URL
    handleHashChange();
    
    // Listen for hash changes (back/forward button)
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [imageUrl, isAuthCallback]);
  
  // Update URL when view changes
  useEffect(() => {
    // Don't update URL for auth callbacks
    if (isAuthCallback) return;
    
    // Update hash based on current view
    window.location.hash = activeView;
  }, [activeView, isAuthCallback]);

  // Sync UI state with activeView
  useEffect(() => {
    // Update UI state based on activeView
    setShowSettings(activeView === 'settings');
    setShowSummary(activeView === 'summary');
    setShowPresets(activeView === 'presets');
  }, [activeView]);

  // Initialize Supabase authentication

  // If this is an auth callback URL, render the AuthCallback component
  if (isAuthCallback) {
    return <AuthCallback />;
  }

  return (
    <PricingProvider>
      <div className="App">
        <header className="App-header">
          <div>
            <h1 
              onClick={() => currentProject ? handleSelectProject(currentProject) : null}
              className={currentProject ? "app-logo clickable" : "app-logo"}
              title={currentProject ? "Return to current project" : ""}
            >
              Romulus
            </h1>
            <span className="version-number">v{APP_VERSION}</span>
          </div>
          
          {user ? (
            <div className="user-info">
              <div className="global-nav">
                <button
                  className={`nav-button ${activeView === 'projects' ? 'active' : ''}`}
                  onClick={() => {
                    setImageUrl(null);
                    setShowSettings(false);
                    setActiveView('projects');
                  }}
                >
                  Projects
                </button>
                {/* Temporarily hiding Simple Editor button
                <button
                  className={`nav-button ${activeView === 'simple' ? 'active' : ''}`}
                  onClick={() => {
                    setImageUrl(null);
                    setShowSettings(false);
                    setShowSummary(false);
                    setShowPresets(false);
                    setActiveView('simple');
                  }}
                >
                  Simple Editor
                </button>
                */}
                <button
                  className={`nav-button ${activeView === 'settings' ? 'active' : ''}`}
                  onClick={handleToggleSettings}
                >
                  Presets
                </button>
              </div>
              <span>{user?.email || user?.user_metadata?.email}</span>
              <button className="logout-button" onClick={handleSignOut}>Sign Out</button>
            </div>
          ) : (
            <div className="auth-buttons">
              <button 
                className={authMode === 'login' ? 'active' : ''} 
                onClick={() => setAuthMode('login')}
              >
                Log In
              </button>
              <button 
                className={authMode === 'signup' ? 'active' : ''} 
                onClick={() => setAuthMode('signup')}
              >
                Sign Up
              </button>
            </div>
          )}
        </header>
        
        <main className="App-main">
          {/* Only show global drag and drop when authenticated and in projects view */}
          {globalDragDropEnabled && (
            <GlobalDragDrop onFileSelect={(file) => {
              handleFileSelect(file);
              setActiveView('viewer');
            }} />
          )}
          
          {/* Authentication Forms */}
          {authMode === 'signup' && (
            <SignUp onSuccess={() => handleAuthSuccess('login')} />
          )}
          
          {authMode === 'login' && (
            <Login onSuccess={handleAuthSuccess} />
          )}
          
          {/* Add Settings Page */}
          {!authMode && showSettings && (
            <SettingsPage />
          )}
          
          {/* Presets Page */}
          {!authMode && showPresets && (
            <AuthGuard onUnauthenticated={handleUnauthenticated}>
              <PresetsPage onBack={handleBackFromPresets} />
            </AuthGuard>
          )}
          
          {/* Project Summary Page */}
          {!authMode && showSummary && (
            <AuthGuard onUnauthenticated={handleUnauthenticated}>
              <ProjectSummaryPage 
                sensorCounts={currentProject?.sensor_counts || {}}
                projectName={fileName || currentProject?.name || 'Untitled Project'}
                projectId={currentProject?.id}
                onBack={handleBackFromSummary}
              />
            </AuthGuard>
          )}
          
          {/* Main Content (protected by AuthGuard) */}
          {!authMode && !showSettings && !showSummary && (
            <AuthGuard onUnauthenticated={handleUnauthenticated}>
              {/* Project List */}
              {!imageUrl && activeView === 'projects' && (
                <ProjectList onSelectProject={handleSelectProject} />
              )}
              
              {/* Simple Floorplan Editor */}
              {/* Temporarily hiding Simple Editor
              {activeView === 'simple' && (
                <SimpleFloorplan />
              )}
              */}
              
              {/* Upload Section */}
              {!imageUrl && activeView === 'upload' && (
                <div className="page-container">
                  <section className="upload-section">
                    <h2>Upload a Floorplan Image</h2>
                    
                    {/* Use the new component for drag & drop and file upload */}
                    <DragDropFileUpload 
                      onFileSelect={handleFileSelect}
                      fileName={fileName}
                    />
                    
                    {/* Display supported formats and tips */}
                    <FileUploadInfo />
                  </section>
                </div>
              )}
              
              {/* Floorplan Viewer (when image is loaded) */}
              {imageUrl && (
                <>
                  {isProcessing ? (
                    <div className="processing-indicator">
                      <div className="spinner"></div>
                      <p>Processing floorplan...</p>
                    </div>
                  ) : (
                    <>
                      <section className="floorplan-section">
                        <div className="floorplan-header">
                          <ProjectNameInput 
                            initialName={fileName || 'Untitled Project'} 
                            onNameChange={handleProjectNameChange}
                          />
                          <SaveStatus status={saveStatus} />
                        </div>
                        
                        <FloorplanViewer
                          imageUrl={imageUrl}
                          initialData={currentProject}
                          onSave={handleSaveProject}
                          onShowSummary={handleShowSummary}
                        />
                      </section>
                    </>
                  )}
                </>
              )}
            </AuthGuard>
          )}
          
          {/* Show Version Changes button at the bottom of the page */}
          <button className="version-button" onClick={() => setShowChangelog(true)}>
            <span className="version-number-button">v{APP_VERSION}</span>
            <span className="version-date">{getCurrentVersionDate()}</span>
          </button>
          
          {/* Version changelog modal */}
          {showChangelog && (
            <Changelog
              version={APP_VERSION}
              changes={getCurrentVersionChanges()}
              onClose={handleCloseChangelog}
            />
          )}
        </main>
      </div>
    </PricingProvider>
  );
}

export default App;
